import { columnState } from "../components/column-state.js";
import { facets, filterAssignments } from "../facets.js";
import { convertFacet } from "../units-conversion.js";
import { attachColumnFormatValue } from "../number-formatting.js";

const FIXED_FIRST = "item-number";
const FIXED_LAST = "price";

/**
 * Build column configuration from column keys
 * @param {Array<string>} columnKeys - Array of column keys
 * @param {boolean} [formatNumbers=false] - Attach formatValue for numeric formatting
 * @param {Object} [assignmentMap={}] - Category displayHits entry per key (for precision/skip overrides)
 * @returns {Array} Array of column configuration objects
 */
function buildColumnsFromKeys(columnKeys, formatNumbers = false, assignmentMap = {}) {
  return columnKeys
    .map((key) => {
      if (!facets[key]) return null;

      const facet = convertFacet(facets[key]);
      const column = {
        ...facet,
        key: key,
        label: facet.label_column ?? facet.label,
      };

      if (facet.sortable) {
        column.ascIndex = `${algolia.product_index_name}_${facet.originalAttribute}_asc`;
        column.descIndex = `${algolia.product_index_name}_${facet.originalAttribute}_desc`;
      }

      if (formatNumbers) {
        attachColumnFormatValue(column, facet, assignmentMap[key] || {});
      }

      return column;
    })
    .filter((col) => col !== null);
}

/**
 * Ensure fixed columns are in correct positions
 * @param {Array<string>} columnKeys - Array of column keys
 * @returns {Array<string>} Array with fixed columns in correct positions
 */
function ensureFixedColumns(columnKeys) {
  const result = [];
  const middleColumns = [];

  // Separate fixed and middle columns
  columnKeys.forEach((key) => {
    if (key === FIXED_FIRST) {
      // Will add at start
    } else if (key === FIXED_LAST) {
      // Will add at end
    } else {
      middleColumns.push(key);
    }
  });

  // Build result: item-number first, middle columns, price last
  result.push(FIXED_FIRST);
  result.push(...middleColumns);
  result.push(FIXED_LAST);

  return result;
}

/**
 * Get default columns for a category from displayHits (same logic as popup)
 * @param {string} category - Category slug
 * @returns {{ columnKeys: Array<string>, assignmentMap: Object }}
 */
function getDefaultColumnsWithFixed(category) {
  const categorySetup = filterAssignments[category] ?? filterAssignments["DEFAULT"];
  
  let columns = [];
  
  // For SERP, use only SERP.displayHits (as in search.js)
  if (category === "SERP") {
    columns = [...(categorySetup.displayHits || [])];
  } else {
    // For other categories, merge category displayHits with EVERY.displayHits (as in product-category.js)
    const everyColumns = filterAssignments["EVERY"]?.displayHits || [];
    columns = [
      ...(categorySetup.displayHits || []),
      ...everyColumns,
    ];
  }
  
  // Sort by orderColumns
  columns.sort((a, b) => a.orderColumns - b.orderColumns);

  // Build assignment map (key → full displayHits entry with precision/skipNumericFormatting)
  const assignmentMap = {};
  columns.forEach((c) => {
    assignmentMap[c.key] = c;
  });

  // Get column keys and ensure fixed columns are in place
  const columnKeys = columns.map((c) => c.key);
  return { columnKeys: ensureFixedColumns(columnKeys), assignmentMap };
}

/**
 * Middleware to handle column configuration from user preferences
 */
export const chamfrColumnConfigMiddleware = function(options = {}) {
  const formatNumbers = options.formatNumbers || false;

  return function({ instantSearchInstance }) {
    let currentCategory = null;
    let columnConfigHandler = null;

    // Load the columns for a category (user preferences, else category
    // defaults) and push them into column state.
    const applyColumnsForCategory = (category) => {
      const userPrefs = columnState.loadUserPreferences(category);

      let columnKeys;
      let assignmentMap = {};

      if (userPrefs && Array.isArray(userPrefs) && userPrefs.length > 0) {
        // Use user preferences
        columnKeys = ensureFixedColumns(userPrefs);
      } else {
        // Use default columns for category from displayHits
        const defaults = getDefaultColumnsWithFixed(category);
        columnKeys = defaults.columnKeys;
        assignmentMap = defaults.assignmentMap;
      }

      const columns = buildColumnsFromKeys(columnKeys, formatNumbers, assignmentMap);
      columnState.setColumns(columns);
    };

    return {
      onStateChange() {},

      subscribe() {
        // Set initial columns based on category
        currentCategory = columnState.getCurrentCategory();
        applyColumnsForCategory(currentCategory);

        // Listen for column config updates from popup
        const handleColumnConfigUpdate = (category, columnKeys) => {
          if (category !== currentCategory) return;

          // Ensure fixed columns are in place
          const orderedKeys = ensureFixedColumns(columnKeys);
          
          // Build column configs (user prefs — only keys, no category-level overrides)
          const columns = buildColumnsFromKeys(orderedKeys, formatNumbers, {});
          
          // Update column state
          columnState.setColumns(columns);
          
          // Save to localStorage
          columnState.saveUserPreferences(category, orderedKeys);
          
          // Trigger refresh to update widgets
          setTimeout(() => {
            instantSearchInstance.refresh();
          }, 0);
        };

        // Store handler for cleanup
        columnConfigHandler = handleColumnConfigUpdate;

        // Expose handler globally so popup can call it
        window.chamfrColumnConfigUpdate = handleColumnConfigUpdate;

        instantSearchInstance.on("render", () => {
          // Check if category changed
          const detectedCategory = columnState.getCurrentCategory();

          if (detectedCategory !== currentCategory) {
            currentCategory = detectedCategory;
            applyColumnsForCategory(detectedCategory);

            // Trigger refresh to update widgets
            setTimeout(() => {
              instantSearchInstance.refresh();
            }, 0);
          }
        });
      },

      unsubscribe() {
        if (window.chamfrColumnConfigUpdate === columnConfigHandler) {
          delete window.chamfrColumnConfigUpdate;
        }
      },
    };
  };
};
